import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV } from "@/content/site";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

export default function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header className="sticky top-0 z-50 bg-[var(--ink)]/90 backdrop-blur-md border-b border-[var(--line)]" data-testid="site-header">
      <div className="mx-auto max-w-[1400px] h-16 md:h-20 px-5 md:px-10 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8" aria-label="Main">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end data-testid={`nav-${slug(n.label)}`}
              className={({ isActive }) => `relative text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors duration-200 ${isActive ? "text-white" : "text-[var(--mute)] hover:text-white"} after:absolute after:-bottom-2 after:left-0 after:h-[2px] after:bg-[var(--red)] after:transition-[width] after:duration-300 ${isActive ? "after:w-full" : "after:w-0 hover:after:w-full"}`}>
              {n.label}
            </NavLink>
          ))}
          <Link to="/request-a-repair" data-testid="nav-request-repair" className="chamfer-sm bg-[var(--red)] hover:bg-[var(--red-hi)] transition-colors px-5 h-11 inline-flex items-center font-display font-extrabold uppercase tracking-[0.14em] text-sm">
            Request a Repair
          </Link>
        </nav>
        <button type="button" className="lg:hidden h-11 w-11 inline-flex items-center justify-center border border-[var(--line)]" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {createPortal(<AnimatePresence>
        {open && (
          <motion.nav initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
            className="lg:hidden fixed z-[48] inset-x-0 top-16 bottom-0 bg-[var(--ink)] grid-lines px-5 pt-8 pb-28 overflow-y-auto" aria-label="Mobile" data-testid="mobile-menu">
            {[...NAV, { label: "Request a Repair", to: "/request-a-repair" }].map((n, i) => (
              <motion.div key={n.to} initial={{ x: -24, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.05 * i, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
                <NavLink to={n.to} end data-testid={`mobile-nav-${slug(n.label)}`} className={({ isActive }) => `flex items-baseline gap-4 py-4 border-b border-[var(--line)] font-display font-extrabold uppercase text-4xl ${isActive ? "text-white" : "text-[var(--grey)]"}`}>
                  <span className="font-mono text-xs text-[var(--red)]">0{i + 1}</span>{n.label}
                </NavLink>
              </motion.div>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>, document.body)}
    </header>
  );
}
